<template>
  <div class="next-destination">
    <p v-if="options?.current" class="text-muted small mb-3">
      Izbrano ob {{ dayjs(options.current.declaredAt).format("HH:mm") }}:
      <strong>{{ options.current.targetName }}</strong>
      <span v-if="options.current.coworkerName">, stranpot za {{ options.current.coworkerName }}</span>
      <span v-if="options.current.isUsed"> (že upoštevano pri odhodu)</span>
    </p>

    <template v-if="options">
      <div class="form-check mb-2">
        <input id="nd-coworker" v-model="withCoworker" class="form-check-input" type="checkbox" :disabled="busy" />
        <label class="form-check-label" for="nd-coworker">👥 Stranpot za sodelavca</label>
      </div>
      <select v-if="withCoworker" v-model="coworkerId" class="form-select mb-3">
        <option :value="null" disabled>Izberite sodelavca</option>
        <option v-for="c in options.coworkers" :key="c.id" :value="c.id">{{ c.name }}</option>
      </select>

      <div class="d-grid gap-2">
        <button
          class="btn btn-success btn-lg text-start"
          type="button"
          :disabled="!options.next || blocked"
          @click="declareAsync('NextPlanned')"
        >
          <span class="d-block fw-semibold">Grem po planu</span>
          <small v-if="options.next" class="d-block">{{ stopText(options.next) }}</small>
          <small v-else class="d-block">Za danes ni več planirane poti.</small>
        </button>

        <button
          class="btn btn-outline-success text-start"
          type="button"
          :disabled="!options.planned.length || blocked"
          @click="showPlanned = !showPlanned"
        >
          ⤼ Preskočim naslednjo, grem na drugo postajo iz plana
        </button>
        <div v-if="showPlanned" class="list-group">
          <button
            v-for="stop in options.planned"
            :key="stop.waypointId"
            class="list-group-item list-group-item-action"
            type="button"
            :disabled="blocked"
            @click="declareAsync('Planned', { waypointId: stop.waypointId })"
          >
            {{ stopText(stop) }}
          </button>
        </div>

        <button
          class="btn btn-outline-primary text-start"
          type="button"
          :disabled="!options.hasHome || blocked"
          @click="declareAsync('Home')"
        >
          🏠 Domov
        </button>
        <button
          class="btn btn-outline-primary text-start text-truncate"
          type="button"
          :disabled="!options.warehouse || blocked"
          @click="declareAsync('Warehouse')"
        >
          🏭 {{ options.warehouse?.name || "Skladišče" }}
        </button>
        <button
          class="btn btn-outline-secondary text-start"
          type="button"
          :disabled="blocked"
          @click="declareAsync('Shopping')"
        >
          🛒 Nabava
        </button>
        <button
          class="btn btn-outline-secondary text-start"
          type="button"
          :disabled="blocked"
          @click="declareAsync('Private')"
        >
          🔒 Privat
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue"
import dayjs from "dayjs"
import { useAuth } from "../composition-api/useAuth"
import { setError, setSuccess, startLoading, stopLoading } from "./Notification"

// "Kam greš?": the worker tells where they go next; the server uses it for the next vehicle departure
// (api/WorkerDestinations.cs, WorkerTripRecorder)
defineOptions({ name: "NextDestination" })

const emit = defineEmits(["declared"])

const { fetchAuthAsync } = useAuth()

const options = ref(null) // { next, planned, hasHome, warehouse, coworkers, current }
const showPlanned = ref(false)
// a detour to pick up a coworker, on top of any destination
const withCoworker = ref(false)
const coworkerId = ref(null)
const busy = ref(false)
const blocked = computed(() => busy.value || (withCoworker.value && !coworkerId.value))

const stopText = (stop) => [stop.time?.slice(0, 5), stop.name, stop.serviceOrderNumber].filter(Boolean).join(" · ")

const loadAsync = async () => {
  startLoading("NextDestination")
  try {
    const raw = await fetchAuthAsync(`${AZURE_URI}/WorkerDestination/options`, "GET")
    if (raw?.status !== "ok") {
      throw raw
    }
    options.value = raw
  } catch (e) {
    setError(e)
  }
  stopLoading("NextDestination")
}

const declareAsync = async (kind, extra = {}) => {
  if (withCoworker.value) extra.coworkerId = coworkerId.value
  busy.value = true
  startLoading("NextDestination")
  try {
    const raw = await fetchAuthAsync(`${AZURE_URI}/WorkerDestination`, "POST", { kind, ...extra })
    if (raw?.status !== "ok") {
      throw raw
    }
    options.value.current = raw.result
    showPlanned.value = false
    withCoworker.value = false
    coworkerId.value = null
    setSuccess({ message: raw.message })
    emit("declared", raw.result)
  } catch (e) {
    setError(e)
  }
  stopLoading("NextDestination")
  busy.value = false
}

onMounted(loadAsync)
</script>
